/**
 * scanOrders — Service-Layer confirm for a scanned Bestellschein.
 *
 * BUG-2026-06-16-RVBDQ part (D). The confirm POST
 * (`POST /v1/scan-orders/{scan_id}/confirm`) pushes the reviewed order to Odoo
 * as a Freigabe. When that push fails with a transient/server error (the live
 * 502 = Odoo SSL cert reissue, SL-owned BUG-2026-06-16-KA45U), the scan must
 * NOT be lost — the write joins the offline-first standard and is queued for
 * replay. This module is the single SL call the offline queue handler replays,
 * so the request shape lives in ONE place (page submit + queue replay agree).
 *
 * The confirm body is the FLAT ScanOrderConfirmRequest
 * ({ customer_external_id, positionen[], … }) the FE already builds in
 * apps/.../scanConfirmRequest.ts; this module is transport-only and does not
 * re-shape it.
 */
import { serviceFetch } from '../http/serviceLayerClient'

/**
 * The queued/replayed confirm payload: the scan id plus the already-built flat
 * ScanOrderConfirmRequest body. Typed as a record because the body shape is
 * owned by the app layer (scanConfirmRequest.ts) — lib stays transport-only.
 */
export interface ScanOrderConfirmPayload {
  scanId: string
  body: Record<string, unknown>
}

/**
 * The durable confirmation receipt: `status` is the producer's own record of
 * the outcome (`ScanOrderConfirmEnvelope.status`, always present on a 2xx),
 * and `odoo_order_id` is the server-assigned Odoo Freigabe id when the push
 * succeeded (nullable — see `confirmScanOrder`).
 */
export interface ScanOrderConfirmReceipt {
  status: string
  odoo_order_id: number | string | null
}

/**
 * POST the reviewed scan order to the SL confirm endpoint and return the
 * confirmation receipt. Throws the raw ServiceHttpError on failure so the
 * caller can route a transient/5xx (incl. the 502) into the offline queue and
 * a genuine 400/422 into a loud failure via `shouldQueueFailedWrite`.
 *
 * DELETE-ON-VERIFIED-ACK (matches the discipline `handlers.visitReports.ts`
 * documents as its own charter, which this door had been violating): this
 * used to discard the response and return void, so the offline replay
 * handler pruned the outbox entry on a bare 2xx with no proof the Odoo push
 * actually landed — the same defect CLASS as the v2 visit-report door (a 2xx
 * accepted, the receipt never read), just on the other side of the receipt
 * (never even attempted here, instead of attempted-and-wrong there).
 */
export async function confirmScanOrder(payload: ScanOrderConfirmPayload): Promise<ScanOrderConfirmReceipt> {
  const { data } = await serviceFetch<unknown>(
    `/v1/scan-orders/${encodeURIComponent(payload.scanId)}/confirm`,
    { method: 'POST', body: JSON.stringify(payload.body) },
  )
  const envelope = (data ?? {}) as Record<string, unknown>
  const rawStatus = envelope.status
  const status = typeof rawStatus === 'string' && rawStatus ? rawStatus : null
  if (!status) {
    // No status in the response ⇒ the write did NOT confirm. Fail loud rather
    // than return a manufactured receipt (D-33 R1/R3) — mirrors submitVisitReportV8.
    throw new Error(
      'scan-order confirm response missing the required `status` field — the '
      + 'write was not confirmed by the service layer',
    )
  }
  const rawOrderId = envelope.odoo_order_id
  const odoo_order_id =
    typeof rawOrderId === 'string' || typeof rawOrderId === 'number' ? rawOrderId : null
  return { status, odoo_order_id }
}
